import React from 'react';
import {
  MonoKicker,
  GoldRule,
  PosterHeadline,
  SerifStory,
  GoldClause,
  DataRow,
  GoldCTA,
  CtaMicroline,
  AppStoreBadge,
  HeroStack,
  HERO_GLOBE,
} from '../index';

/**
 * §00 HERO — the three-second hook.
 *
 * Three things must be present without scrolling at 390×740: the poster
 * headline, the primary CTA, and the globe. Everything else is subordinate.
 *
 * Every string traces to REAL_STORY_FACTS.md:
 *   460 ............... 460 crossed paths found before meeting
 *   different states .. Nick in Chicago, Michelle in New York; never the
 *                       same city
 *   six years ......... date range 2018–2024
 *   800 FT · CHICAGO · JUNE 2019 ... the closest encounter, June 7 2019.
 *                       This is the ONLY distance figure permitted anywhere
 *                       on this site. No other figure, no metric conversion,
 *                       no venue, no street.
 *
 * The NICK & MICHELLE label is load-bearing: it is what stops 460 from
 * reading as a promise to the visitor. Do not drop it to save a line.
 *
 * Motion is CSS with animation-delay and fill-mode both, so the headline is
 * painted in the first frame. No JS gate may hold the LCP text at opacity 0.
 */
export function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero"
      aria-labelledby="hero-headline"
      className="band-nebula-void relative isolate overflow-x-clip"
    >
      {/* Height rules live ENTIRELY here, never as an inline style. An inline
          min-height outranks every rule in this block, so the svh fallbacks
          and the per-breakpoint overrides below were all silently dead while
          a hardcoded 100vh governed every width — which is why the md
          section measured 1024px instead of collapsing to its content.
          svh keeps the CTA above the fold under mobile URL bars; vh is the
          fallback for browsers that do not know svh. */}
      <style>{`
        #hero { min-height: 100vh; }
        @supports (height: 100svh) {
          #hero { min-height: 100svh; }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          /* Content-driven in the md range. With the globe band and the type
             offset both fixed in px, a viewport-height minimum only adds
             trailing space after the CTA — and it was a percentage OF that
             minimum which starved the crop and opened the dead void. */
          #hero { min-height: auto; }
        }
        @media (min-width: 1024px) {
          #hero { min-height: 88svh; max-height: 940px; }
        }
      `}</style>

      {/* ---------------------------------------------------- globe layer */}
      {/* Absolutely positioned: it never consumes flow height, so the type
          budget below is exact. aria-hidden — the headline carries meaning.
          At ≥1024 the axis rotates to horizontal (globe right, type left) so
          the headline holds the first-read position.

          HEIGHT IS FIXED px, NOT A PERCENTAGE. It used to be 38% of the band,
          which is fine at 390 and breaks badly at 768: 38% of a 1024-tall
          viewport is ~389px of globe, and because object-cover scales the
          image up with WIDTH, a taller box at a wider viewport shows LESS of
          the image vertically — so the arc and CHICAGO fell out of the crop
          entirely and 59% of the fold was globe-with-no-spectacle plus dead
          void. A percentage of viewport height only misbehaves in the
          tall-and-narrow case, which is exactly the case nobody was sweeping.
          Fixed px removes the coupling: the crop is now the same slice of the
          image at every width, and the type offset below is derived from the
          same number.

          Width is capped at 680: hero-globe.webp is a 2× upscale of a 445px
          native crop, so past ~620–680 CSS px it visibly softens. */}
      <div
        aria-hidden="true"
        data-testid="hero-globe"
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[300px] overflow-hidden md:h-[340px] lg:inset-y-0 lg:left-auto lg:right-[-40px] lg:h-full lg:w-[min(680px,48vw)]"
      >
        {/* The crop is a rectangle with its own near-black ground, so its
            edges would otherwise read as a visible box against the band's
            nebula. The mask dissolves the meeting edges — bottom on phones,
            bottom and left on desktop — so the globe sits in the void
            instead of on top of it. No filter touches the pixels. */}
        <img
          src={HERO_GLOBE.src}
          alt=""
          width={HERO_GLOBE.width}
          height={HERO_GLOBE.height}
          fetchpriority="high"
          decoding="async"
          className="arrive-globe hero-globe-img block h-full w-full object-cover object-[50%_32%] md:object-[50%_45%] lg:object-[38%_50%]"
        />
      </div>

      {/* Scrim — the type's legibility guarantee. A sibling overlay across
          the whole band, never a filter on the image and never boxed to it:
          a scrim clipped to the image would draw its own hard edge. Falls to
          the bottom on phones (type below the globe) and to the left at
          ≥1024 (type beside it).
          The phone ramp is deliberately steep and early. Measured worst case
          at 390×740 the kicker sat at 1.13:1 against a bright dot-matrix
          pixel; the requirement is ≥0.90 composite void by 28% of the band,
          which is where the kicker's top edge lands. This is
          viewport-HEIGHT dependent — at 390×900 the same type clears easily
          because it falls below the globe — so it is tuned at 740 and
          checked at 667, never in a tall window. */}
      <div aria-hidden="true" className="hero-scrim pointer-events-none absolute inset-0 z-[1] lg:hidden" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-[1] hidden lg:block"
        style={{
          background:
            'linear-gradient(to left, transparent 0%, rgba(5,5,14,0.30) 34%, rgba(5,5,14,0.80) 52%, #05050E 68%)',
        }}
      />

      {/* ----------------------------------------------------- type column */}
      <div className="relative z-10 mx-auto flex min-h-[inherit] w-full max-w-[1200px] px-5 pb-10 pt-[225px] md:px-10 md:pt-[380px] xl:px-16 lg:items-center lg:pb-6 lg:pt-[72px]">
        <HeroStack className="w-full max-w-[560px] lg:max-w-[520px]">
          {/* ① kicker */}
          <GoldRule className="arrive d-120" animated />
          <MonoKicker className="arrive d-120 mt-2">TWO STRANGERS · ONE MAP</MonoKicker>

          {/* ② poster headline — both lines in one h1 so the accessible
              name reads "460 times before hello" */}
          <PosterHeadline
            as="h1"
            id="hero-headline"
            data-testid="hero-headline"
            className="mt-3.5"
            arrive
            lines={[
              { text: '460 times' },
              { text: 'before hello', outline: true },
            ]}
          />

          {/* ③ serif story line — final clause gold.
              w-full matters: the stack is items-start, so without it a text
              block sizes to max-content and overruns the gutter. */}
          <SerifStory className="arrive d-420 mt-[18px] w-full max-w-[46ch]">
            Nick and Michelle lived in different states. Their paths kept crossing for six
            years — <GoldClause>before they ever said hello.</GoldClause>
          </SerifStory>

          {/* ④ data row. The space before 2019 is U+00A0 — at 390 the value
              wraps, and a normal space orphaned "2019" onto its own line,
              splitting the date between month and year. */}
          <DataRow
            className="arrive d-540 mt-5 w-full"
            label="Nick &amp; Michelle"
            value={'Closest 800 ft · Chicago · June 2019'}
          />

          {/* ⑤ CTA */}
          <div className="arrive d-660 mt-6 flex w-full flex-col items-start gap-4">
            <div className="flex w-full flex-col items-stretch gap-4 min-[560px]:w-auto min-[560px]:flex-row min-[560px]:items-center">
              <GoldCTA data-testid="hero-download" fullWidth />
              <AppStoreBadge data-testid="hero-badge" />
            </div>
            <CtaMicroline className="w-full" />
          </div>
        </HeroStack>
      </div>
    </section>
  );
}

export default Hero;
